import { z } from 'zod';
import { holidayRegionSchema } from './calendar/holidays.js';
import { visibilitySchema } from './permissions.js';
import { isoDateSchema, scheduleModeSchema, statusCategorySchema } from './projects.js';
import { dependencyTypeSchema, taskSchema, taskTypeSchema } from './tasks.js';

/** GANTT-01..09: dependencies, scheduling results and the Gantt view of a project. */

/** Working days; a year either way is plenty. */
export const lagDaysSchema = z.number().int().min(-250).max(250);

/** German names of the dependency types (GANTT-04), for texts the server writes. */
export const dependencyTypeLabels = {
  FS: 'Ende → Anfang',
  SS: 'Anfang → Anfang',
  FF: 'Ende → Ende',
  SF: 'Anfang → Ende',
} as const;

/** Problem `type` URIs of dependencies, so the UI can show specific messages. */
export const dependencyProblemTypes = {
  cycle: 'urn:pm-tool:problem:dependency-cycle',
  duplicate: 'urn:pm-tool:problem:dependency-duplicate',
  otherProject: 'urn:pm-tool:problem:dependency-other-project',
  /** Tasks with subtasks take their dates from the subtasks and have no dependencies. */
  summaryTask: 'urn:pm-tool:problem:dependency-summary-task',
} as const;

export const dependencySchema = z.object({
  id: z.uuid(),
  predecessorId: z.uuid(),
  successorId: z.uuid(),
  type: dependencyTypeSchema,
  lagDays: z.number().int(),
});
export type Dependency = z.infer<typeof dependencySchema>;

export const createDependencyBodySchema = z
  .object({
    predecessorId: z.uuid(),
    successorId: z.uuid(),
    type: dependencyTypeSchema.default('FS'),
    lagDays: lagDaysSchema.default(0),
  })
  .refine((v) => v.predecessorId !== v.successorId, {
    message: 'A task cannot depend on itself',
    path: ['successorId'],
  });
export type CreateDependencyBody = z.infer<typeof createDependencyBodySchema>;
/** Request body before defaults (type FS, lag 0). */
export type CreateDependencyInput = z.input<typeof createDependencyBodySchema>;

export const updateDependencyBodySchema = z.object({
  type: dependencyTypeSchema.optional(),
  lagDays: lagDaysSchema.optional(),
});
export type UpdateDependencyBody = z.infer<typeof updateDependencyBodySchema>;

export const dependencyIdParamsSchema = z.object({ dependencyId: z.uuid() });

// --- Scheduling results (GANTT-05) -------------------------------------------------------------

export const scheduleConflictSchema = z.object({
  dependencyId: z.uuid(),
  successorId: z.uuid(),
  field: z.enum(['startDate', 'dueDate']),
  /** Earliest value the successor's field must have. */
  earliest: isoDateSchema,
});
export type ScheduleConflictDto = z.infer<typeof scheduleConflictSchema>;

export const scheduledTaskSchema = z.object({
  id: z.uuid(),
  startDate: isoDateSchema.nullable(),
  dueDate: isoDateSchema.nullable(),
  progress: z.number().int(),
});

/**
 * Consequences of a date or dependency change, limited to tasks the viewer can see: tasks whose
 * dates or progress changed (the task itself, shifted successors in mode `auto`, parents with
 * derived dates) and violated dependencies touching the changed tasks.
 */
export const scheduleResultSchema = z.object({
  changedTasks: z.array(scheduledTaskSchema),
  conflicts: z.array(scheduleConflictSchema),
});
export type ScheduleResult = z.infer<typeof scheduleResultSchema>;

/** PATCH /tasks/:id: the task plus the scheduling consequences. */
export const updateTaskResponseSchema = taskSchema.extend({ schedule: scheduleResultSchema });

export const dependencyResponseSchema = z.object({
  dependency: dependencySchema,
  schedule: scheduleResultSchema,
});

export const dependencyListResponseSchema = z.object({ items: z.array(dependencySchema) });
export type DependencyListResponse = z.infer<typeof dependencyListResponseSchema>;

// --- Gantt view (GANTT-01..09) -----------------------------------------------------------------

export const ganttTaskSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  parentId: z.uuid().nullable(),
  type: taskTypeSchema,
  title: z.string(),
  statusCategory: statusCategorySchema,
  visibility: visibilitySchema,
  startDate: isoDateSchema.nullable(),
  dueDate: isoDateSchema.nullable(),
  /** Effective progress: 100 in a `done` status, derived for tasks with subtasks. */
  progress: z.number().int(),
  hasSubtasks: z.boolean(),
});
export type GanttTask = z.infer<typeof ganttTaskSchema>;

/**
 * All visible tasks of the project (in tree order), dependencies between visible tasks and the
 * current conflicts. `scheduleMode` is an internal setting: null for external viewers.
 */
export const ganttResponseSchema = z.object({
  tasks: z.array(ganttTaskSchema),
  dependencies: z.array(dependencySchema),
  conflicts: z.array(scheduleConflictSchema),
  scheduleMode: scheduleModeSchema.nullable(),
  holidayRegion: holidayRegionSchema,
  /** More tasks than TASK_LIST_LIMIT: the Gantt shows the first ones only. */
  truncated: z.boolean(),
});
export type GanttResponse = z.infer<typeof ganttResponseSchema>;

// --- GANTT-12: move the project plan ------------------------------------------------------------

export const planShiftBodySchema = z.object({
  /** Working days, negative = earlier; at most a year either way. */
  workingDays: z
    .number()
    .int()
    .min(-250)
    .max(250)
    .refine((n) => n !== 0, { message: 'Mindestens ein Arbeitstag' }),
  /** Only tasks starting (or due) on or after this date; without: the whole plan. */
  from: isoDateSchema.optional(),
});
export type PlanShiftBody = z.infer<typeof planShiftBodySchema>;

const shiftedDatesSchema = z.object({
  startDate: isoDateSchema.nullable(),
  dueDate: isoDateSchema.nullable(),
});

/** What a plan shift changes (preview) or changed (apply); visible tasks only. */
export const planShiftResultSchema = z.object({
  /** Tasks moved directly (open, without subtasks, from the date on). */
  moved: z.array(
    z.object({
      id: z.uuid(),
      key: z.string(),
      title: z.string(),
      milestone: z.boolean(),
      from: shiftedDatesSchema,
      to: shiftedDatesSchema,
    }),
  ),
  schedule: scheduleResultSchema,
});
export type PlanShiftResult = z.infer<typeof planShiftResultSchema>;
